import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { INSIGHT_RANGES } from "../../../shared/goals/insights";
import { useRail } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Chevron, PnRow } from "../../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../../shared/phone/Sheet";
import type { Spot } from "../../../shared/goals/placements";
import { Icon, type IconName } from "../../components/ui/Icon";
import type { Dates, Need } from "../../lib/ccApi";
import type { GoalView } from "../../lib/goalsApi";
import { cardNumbers, placeNumbers, postNumbers } from "../results/shapes";
import { fieldValue, type Field, type Main } from "../results/metrics";
import { useGoalChat } from "./GoalChat";
import { funnelSteps, metricGroups, pctOf, reviewPill, SHAPE, Title, useGoalNumbers, type Fmt } from "./GoalPage";
import { Mark } from "./Layout";
import { NeedSheet } from "./NeedSheet";
import { spotsPill } from "./PlaceSheet";
import { ReadyNative } from "./ReadySteps";
import { adsDay, dayLabel, fmt, hm, needText, parseTime, shortGoal, usd, usd0 } from "./text";
import { datesOf, dayMon, daysWords, type PaceLine } from "./v2";
import { useCan } from "../../context/AuthContext";

/** A goal's status as the phone's pill: its colour and its words. */
function statusOf(state: string, p: PaceLine | null): [string, string] | null {
  if (state === "paused") return ["warn", "Paused"];
  if (state === "getting_ready") return ["warn", "Getting ready"];
  if (state === "planning" || state === "plan_ready") return ["violet", "New plan"];
  if (!p) return null;
  return p.kind === "on" ? ["good", "On pace"] : p.kind === "behind" ? ["warn", "Behind"] : ["", "Finished"];
}

export function Pill({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span className={tone ? `pn-pill ${tone}` : "pn-pill"}>
      <i />
      {children}
    </span>
  );
}

/** A need's icon colour: changes to the ads and the plan in violet, the rest in teal. */
const needTone = (n: Need) => (/^(ad\.|goal\.|audience\.|placements\.|budget\.)/.test(n.kind) ? "violet" : undefined);

/** True once the screen has scrolled past `px`, so its bar takes the title. */
export function useScrolledPast(ref: React.RefObject<HTMLElement | null>, px: number): boolean {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const scroller = ref.current?.closest<HTMLElement>(".pn-pane, .pn-main");
    if (!scroller) return;
    const read = () => setPast(scroller.scrollTop > px);
    read();
    scroller.addEventListener("scroll", read, { passive: true });
    return () => scroller.removeEventListener("scroll", read);
  }, [ref, px]);
  return past;
}

const labelOf = (d: Dates) => ("preset" in d ? (INSIGHT_RANGES.find(([k]) => k === d.preset)?.[1] ?? "") : "Custom");
const today = () => new Date().toISOString().slice(0, 10);

/** Which days the numbers are for: each choice with its days, and a custom From and To. */
function RangeSheet({ open, onClose, dates, launchedAt, onPick }: { open: boolean; onClose: () => void; dates: Dates; launchedAt: string | null; onPick: (d: Dates) => void }) {
  const [from, setFrom] = useState("from" in dates ? dates.from : "");
  const [to, setTo] = useState("to" in dates ? dates.to : today());
  const ok = !!from && !!to && from <= to;
  return (
    <Sheet open={open} onClose={onClose} title="Show numbers for">
      <div className="pn-grp">
        {INSIGHT_RANGES.map(([k, l]) => (
          <button
            type="button"
            key={k}
            className="pn-row"
            onClick={() => {
              onPick({ preset: k });
              onClose();
            }}
          >
            <span className="m">
              <span className="t">{l}</span>
              <span className="s">{daysWords(datesOf({ preset: k }, launchedAt))}</span>
            </span>
            {"preset" in dates && dates.preset === k && (
              <span className="gp-tick">
                <Icon name="check" />
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="pn-cap gp-cap-gap">Pick the days</div>
      <div className="pn-grp">
        <div className="pn-irow">
          <label htmlFor="gp-from">From</label>
          <input id="gp-from" type="date" value={from} max={today()} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="pn-irow">
          <label htmlFor="gp-to">To</label>
          <input id="gp-to" type="date" value={to} max={today()} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>
      <div className="pn-pad gp-sheet-act">
        <button
          type="button"
          className="pn-btn soft wide"
          disabled={!ok}
          onClick={() => {
            onPick({ from, to });
            onClose();
          }}
        >
          Show these days
        </button>
      </div>
    </Sheet>
  );
}

/** Meta's numbers as grouped rows: each name on the left, its value on the right. */
function MetricRows({ m, f, row, perAd, video, post, added, fields, onRemove }: { m: Main; f: Fmt; row: Parameters<typeof metricGroups>[0]["row"]; perAd: boolean; video: string | null; post: ReturnType<typeof postNumbers> | null; added: string[]; fields: Map<string, Field>; onRemove: (k: string) => void }) {
  return (
    <>
      {metricGroups({ m, f, row, perAd, video, post }).map((g) => (
        <section key={g.name}>
          <div className="pn-cap">{g.name}</div>
          <div className="pn-grp">
            {g.rows.map(([n, v]) => (
              <PnRow key={n} title={n} value={v} />
            ))}
          </div>
        </section>
      ))}
      {added.length > 0 && (
        <section>
          <div className="pn-cap">Added by you</div>
          <div className="pn-grp">
            {added.map((k) => {
              const def = fields.get(k);
              return (
                <div className="pn-row static" key={k}>
                  <span className="m">
                    <span className="t">{def?.name ?? k}</span>
                  </span>
                  <span className="v">{f.field(def ?? { kind: "count" }, fieldValue(row, k))}</span>
                  <button type="button" className="gp-x" onClick={() => onRemove(k)} aria-label={`Remove ${def?.name ?? k}`}>
                    <Icon name="x" />
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}

/** Every other number Meta reports, to add to every ad and to all the ads together. */
function AddNumberSheet({ open, onClose, fields, added, onToggle }: { open: boolean; onClose: () => void; fields: Map<string, Field>; added: string[]; onToggle: (k: string) => void }) {
  const all = [...fields.values()];
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Add a number"
      right={
        <button type="button" className="pn-tbtn strong" onClick={onClose}>
          Done
        </button>
      }
      tall
    >
      <p className="gp-lede">Added numbers show on every ad and on all the ads together.</p>
      {[...new Set(all.map((x) => x.group))].map((grp) => (
        <section key={grp} className="gp-sec">
          <div className="pn-cap">{grp}</div>
          <div className="pn-grp">
            {all
              .filter((x) => x.group === grp)
              .map((x) => (
                <button type="button" key={x.key} className="pn-row wrap" role="checkbox" aria-checked={added.includes(x.key)} onClick={() => onToggle(x.key)}>
                  <span className="m">
                    <span className="t">{x.name}</span>
                  </span>
                  {added.includes(x.key) && (
                    <span className="gp-tick">
                      <Icon name="check" />
                    </span>
                  )}
                </button>
              ))}
          </div>
        </section>
      ))}
    </Sheet>
  );
}

/** Where the ads show: Automatic, the one choice while Meta retires choosing spots (as the computer's sheet says). */
function PlaceNative({ open, onClose, ads, spots, onSave }: { open: boolean; onClose: () => void; ads: number; spots: readonly Spot[] | null; onSave: (s: Spot[] | null) => Promise<boolean> }) {
  const had = !!spots?.length;
  const [auto, setAuto] = useState(!had);
  const [busy, setBusy] = useState(false);
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Where the ads show"
      foot={
        had && auto ? (
          <>
            <p className="gp-foot-note">Meta's learning starts again after this change.</p>
            <button
              type="button"
              className="pn-btn pri wide"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                const done = await onSave(null);
                setBusy(false);
                if (done) onClose();
              }}
            >
              Save
            </button>
          </>
        ) : undefined
      }
    >
      <p className="gp-lede">{ads > 1 ? `For all ${ads} ads in this goal.` : "For all the ads in this goal."}</p>
      <div className="pn-grp" role="radiogroup" aria-label="Where the ads show">
        <button type="button" className="pn-row wrap" role="radio" aria-checked={auto} onClick={() => setAuto(true)}>
          <span className="m">
            <span className="t">
              Automatic <span className="gp-rec">Recommended</span>
            </span>
            <span className="s">Meta picks the spots and moves the money to where results cost less.</span>
          </span>
          {auto && (
            <span className="gp-tick">
              <Icon name="check" />
            </span>
          )}
        </button>
        <div className="pn-row static wrap gp-off" role="radio" aria-checked={!auto} aria-disabled="true">
          <span className="m">
            <span className="t">Choose the spots</span>
            <span className="s">
              Meta is retiring spot choices on ad sets.{" "}
              <a href="https://www.socialmediatoday.com/news/meta-removes-option-to-exclude-ad-placements/828461/" target="_blank" rel="noreferrer">
                Read more
              </a>
            </span>
          </span>
        </div>
      </div>
    </Sheet>
  );
}

/** Numbers as tiles, two to a row on a phone and four across a wide screen. */
function Tiles({ items }: { items: [string, string, string?][] }) {
  return (
    <div className="gp-stats">
      {items.map(([v, l, cls]) => (
        <div key={l} className={cls}>
          <b>{v}</b>
          <span>{l}</span>
        </div>
      ))}
    </div>
  );
}

/** The pace and the money, the prototype's pace card: done in teal, where it lands striped, a mark for today. */
function PaceCard({ p, label, spentCents, budgetCents }: { p: PaceLine; label: string; spentCents: number; budgetCents: number }) {
  const w = (v: number) => Math.max(0, Math.min(100, (100 * v) / Math.max(1, p.target)));
  const words = p.kind === "done" ? `Finished ${dayMon(p.end)}` : p.kind === "on" ? `On pace for about ${fmt(p.forecast)} by ${dayMon(p.end)}` : `At this rate, about ${fmt(p.forecast)} by ${dayMon(p.end)}`;
  const time = (100 * p.gone) / p.all;
  const money = Math.min(100, (100 * spentCents) / Math.max(1, budgetCents));
  return (
    <section className="pn-card gp-pace" aria-label="Pace and money">
      <div className="gp-pt">
        <b className="gp-big">{fmt(p.got)}</b>
        <span>
          of {fmt(p.target)} {label}
        </span>
      </div>
      <p className={`gp-fc ${p.kind}`}>{words}</p>
      <div className="gp-bar" role="img" aria-label={`${fmt(p.got)} of ${fmt(p.target)} ${label}. ${words}.`}>
        {p.kind !== "done" && <i className="gp-ghost" style={{ left: `${w(p.got)}%`, width: `${Math.max(0, w(p.forecast) - w(p.got))}%` }} />}
        <i className="gp-fill" style={{ width: `${w(p.got)}%` }} />
        {p.kind !== "done" && <u style={{ left: `${w(p.expected)}%` }} />}
      </div>
      <div className="gp-ends">
        <span>{dayMon(p.start)}</span>
        {p.kind !== "done" && (
          <span className="gp-today" style={{ left: `${w(p.expected)}%` }}>
            Today
          </span>
        )}
        <span>{dayMon(p.end)}</span>
      </div>
      <div className="gp-sp">
        <div className="gp-pt">
          <b className="gp-mid">{usd0(spentCents)}</b>
          <span>of {usd(budgetCents)} spent</span>
        </div>
        <div className="gp-bar thin">
          <i className="gp-fill" style={{ width: `${money}%` }} />
          {p.kind !== "done" && <u style={{ left: `${time}%` }} />}
        </div>
        <p className="gp-sm">{p.kind === "done" ? `${pctOf(spentCents, budgetCents)}% of the money spent.` : `${pctOf(spentCents, budgetCents)}% of the money for ${pctOf(p.gone, p.all)}% of the time. ${p.all - p.gone} days left.`}</p>
      </div>
    </section>
  );
}

/**
 * A goal's page on a phone and an iPad (the prototype's `goal`): the goal's own words as the title, what needs
 * you first, then the pace and the money, the numbers and the ads. The plan, Change the goal and everything the
 * agent did sit at the bottom and in the … menu; Ask opens the goal's chat. On an iPad it also fits Home's pane.
 */
export function GoalNative({ view, reload }: { view: GoalView; reload: () => Promise<void> }) {
  const g = view.goal;
  const goalPath = `/goals/${encodeURIComponent(g.id)}`;
  const navigate = useNavigate();
  const rail = useRail();
  const n = useGoalNumbers(view, reload);
  const c = useGoalChat(view, reload);
  const can = useCan();
  const { config, label, product, homeGoal, dates, setDates, data, ready, steps, busy, error, added, running, chosen, saveSpots, load, f, fields, toggleField, dateLabel, inWords, p, spentCents, ads, setMain, perAd, videos, live, cheapest, spots, over } = n;
  const { needs, panel, setPanel, wrap, setWrap, decide, pause, review, wrapBody, wrapTitle } = c;

  const screen = useRef<HTMLDivElement>(null);
  const scrolled = useScrolledPast(screen, 70);
  const [sheet, setSheet] = useState<null | "more" | "pause" | "resume" | "range" | "all" | "add" | "place">(null);
  const [adOpen, setAdOpen] = useState<string | null>(null);
  // Why: Add a number opens from all the numbers or from one ad, and goes back to where it came from.
  const [addBack, setAddBack] = useState<"all" | null>(null);
  const close = () => setSheet(null);
  const addNumber = (from: "all" | null) => {
    setAddBack(from);
    setSheet("add");
  };

  const status = statusOf(g.state, p);
  const canEdit = (g.state === "live" || g.state === "paused" || g.state === "getting_ready") && can("editor");
  const version = view.current?.version ?? g.version;
  const short = shortGoal(g.sentence);
  const explain = (text: string, adId?: string) => navigate(`${goalPath}/chat`, { state: { ask: { text, of: { ...dates, ...(adId ? { adId } : {}) } } } });

  const moreActions = [
    ...(g.state !== "done" ? [{ label: "The plan", onSelect: () => navigate(`${goalPath}/plan`) }] : []),
    ...(canEdit ? [{ label: "Change the goal", onSelect: () => navigate(`${goalPath}/edit`) }] : []),
    ...(g.state === "done" ? [{ label: "Wrap-up", onSelect: () => setWrap(true) }] : []),
    { label: "Everything the agent did", onSelect: () => navigate(`${goalPath}/log`) },
    ...(g.state === "live" && can("editor") ? [{ label: "Pause the goal", danger: true, onSelect: () => setSheet("pause") }] : []),
    ...(g.state === "paused" && can("admin") ? [{ label: "Resume the goal", onSelect: () => setSheet("resume") }] : []),
  ];

  const ad = adOpen ? ads.find((a) => a.platformAdId === adOpen) ?? null : null;
  const adIdx = ad ? ads.indexOf(ad) : -1;

  const head = (
    <header className="gp-head">
      {product && (
        <Link className="gp-prod" to={`/products/${encodeURIComponent(product.id)}`}>
          <Mark p={product} />
          <span>{product.name}</span>
        </Link>
      )}
      <h1 className="gp-title">
        <Title sentence={g.sentence} />
      </h1>
      <div className="gp-meta">
        {status && <Pill tone={status[0]}>{status[1]}</Pill>}
        {config && (
          <span>
            {usd(config.money.budgetCents)} budget, most {usd(config.money.perResultMaxCents)} each
          </span>
        )}
        {!g.launchedAt && homeGoal && adsDay(homeGoal) ? <span>Ads go live {adsDay(homeGoal)}</span> : null}
      </div>
    </header>
  );

  const needsPart = needs.length > 0 && (
    <section aria-label="Needs you">
      <div className="pn-sh">
        <h2>
          Needs you <span className="gp-n">{needs.length}</span>
        </h2>
      </div>
      <div className="pn-grp gp-needrows">
        {needs.map((x) => {
          const t = needText(x);
          const why = typeof x.payload.reason === "string" && x.payload.reason ? x.payload.reason : t.line;
          return <PnRow key={x.id} onClick={() => setPanel({ focus: x.id })} icon={<Icon name={t.icon} />} iconTone={needTone(x)} title={t.title} sub={why || undefined} wrap chevron />;
        })}
      </div>
    </section>
  );

  const setupPart =
    g.state === "getting_ready" ? (
      <ReadyNative goalPath={goalPath} steps={steps} ready={ready} view={view} />
    ) : g.state === "planning" || g.state === "plan_ready" ? (
      <section className="pn-card gp-next">
        <span className="pn-ico violet">
          <Icon name="doc" />
        </span>
        <div>
          <h3>{g.state === "plan_ready" ? "A new plan is ready" : "The agent is writing a new plan"}</h3>
          <Link className={`pn-btn sm${g.state === "plan_ready" ? " pri" : " soft"}`} to={`${goalPath}/edit`}>
            {g.state === "plan_ready" ? "Review the plan" : "New plan"}
          </Link>
        </div>
      </section>
    ) : null;

  const numbers =
    data === undefined ? (
      running ? <p className="pn-note gp-note">Reading Meta's numbers…</p> : null
    ) : data === null ? (
      running ? (
        <section className="pn-card gp-empty">
          <h3>No numbers yet</h3>
          <p>Results show here once the ads run on Meta.</p>
        </section>
      ) : null
    ) : (
      <>
        <section aria-label="Numbers">
          <div className="pn-sh">
            <h2>Numbers</h2>
            <button type="button" className="pn-lnk gp-range" onClick={() => setSheet("range")} aria-label={`Numbers for ${labelOf(dates).toLowerCase()}. Change the days`}>
              {labelOf(dates)}
              <Icon name="chev" />
            </button>
          </div>
          <Tiles
            items={[
              [f.money(setMain.spend), "Spent"],
              [f.count(setMain.results), "Results"],
              [f.money(setMain.costEach), "Each", over ? "warn" : undefined],
              [f.count(setMain.reach), "Saw the ads"],
            ]}
          />
          {over && config && <p className="pn-note gp-warn-note">Over your {usd(config.money.perResultMaxCents)} most per result {inWords}.</p>}
          {data.stale && (
            <p className="pn-note">
              Meta could not be read just now. These are the numbers read {dayLabel(parseTime(data.readAt)).toLowerCase()} at {hm(parseTime(data.readAt))}.
            </p>
          )}
          <div className="pn-grp gp-g2">
            <PnRow onClick={() => setSheet("all")} icon={<Icon name="chart" />} iconTone="plain" title="All of Meta's numbers" chevron />
          </div>
        </section>
        {ads.length > 0 && (
          <section aria-label="Ads">
            <div className="pn-sh">
              <h2>Ads</h2>
              {canEdit && (
                <Link className="pn-lnk" to={`${goalPath}/ads`}>
                  All ads
                </Link>
              )}
            </div>
            <div className="pn-grp">
              {ads.map((a, i) => {
                const m = perAd[i];
                const shape = a.shape ? SHAPE[a.shape] : (["play", "Added outside Flashadz"] as [string, string]);
                const pill = reviewPill(a);
                const ran = (m.impressions ?? 0) > 0 && !!a.row;
                return (
                  <button type="button" className="pn-row gp-ad" key={a.platformAdId} onClick={() => setAdOpen(a.platformAdId)}>
                    <span className="gp-thumb">
                      <Icon name={shape[0] as IconName} />
                    </span>
                    <span className="m">
                      <span className="t">{a.name}</span>
                      <span className="s">
                        {ran ? (
                          <>
                            <b>{f.money(m.costEach)}</b> each · {f.count(m.results)} {m.results === 1 ? "result" : "results"}
                            {i === cheapest && <span className="gp-best"> · Cheapest</span>}
                          </>
                        ) : pill ? (
                          <span className={`gp-st ${pill[0]}`}>{pill[1]}</span>
                        ) : (
                          `No numbers ${inWords}`
                        )}
                      </span>
                    </span>
                    {ran && pill && <span className={`gp-st ${pill[0]}`}>{pill[1]}</span>}
                    <Chevron />
                  </button>
                );
              })}
            </div>
          </section>
        )}
        <section aria-label="Where they showed">
          <div className="pn-sh">
            <h2>Where they showed</h2>
          </div>
          <div className="pn-grp">
            {spots.slice(0, 3).map((s) => (
              <PnRow key={s.key} title={s.name} sub={`${Math.round(s.share * 100)}% of the money`} value={s.costEach === null ? "No results" : `${f.money(s.costEach)} each`} />
            ))}
            <PnRow onClick={can("editor") ? () => setSheet("place") : undefined} title="Where the ads show" sub={spotsPill(chosen)} chevron={can("editor")} />
          </div>
        </section>
      </>
    );

  const bottom = (
    <section>
      <div className="pn-grp">
        {g.state !== "done" && <PnRow to={`${goalPath}/plan`} icon={<Icon name="doc" />} iconTone="plain" title="The plan" value={`Version ${version}`} />}
        {canEdit && <PnRow to={`${goalPath}/edit`} icon={<Icon name="pen" />} iconTone="plain" title="Change the goal" />}
        {g.state === "done" && <PnRow onClick={() => setWrap(true)} icon={<Icon name="doc" />} iconTone="plain" title="Wrap-up" chevron />}
        <PnRow to={`${goalPath}/log`} icon={<Icon name="list" />} iconTone="plain" title="Everything the agent did" />
      </div>
    </section>
  );

  return (
    <div className={`pn-screen gp-goal${scrolled ? " gp-scrolled" : ""}`} ref={screen}>
      <PhoneNav
        title={<span>{short}</span>}
        back={rail ? undefined : { to: "/command", label: "Home" }}
        actions={
          <>
            <Link className="pn-tbtn word" to={`${goalPath}/chat`} aria-label="Ask the agent about this goal">
              <Icon name="ask" />
              Ask
            </Link>
            <button type="button" className="pn-tbtn" onClick={() => setSheet("more")} aria-label="More for this goal">
              <Icon name="more" />
            </button>
          </>
        }
      />
      <div className="pn-stack gp-stack">
        {head}
        {error && <p className="pn-note gp-warn-note">{error}</p>}
        {needsPart}
        {setupPart}
        {p && config && <PaceCard p={p} label={label} spentCents={spentCents} budgetCents={config.money.budgetCents} />}
        {numbers}
        {bottom}
      </div>

      <ActionSheet open={sheet === "more"} onClose={close} title={short} actions={moreActions} />
      <ActionSheet
        open={sheet === "pause"}
        onClose={close}
        title="Pause the goal?"
        message="The ads turn off and no emails or replies go out. The agent keeps reading results, and nothing is deleted."
        actions={[{ label: "Pause everything", danger: true, onSelect: () => void pause(true) }]}
      />
      <ActionSheet open={sheet === "resume"} onClose={close} title="Resume the goal?" message="The ads turn back on, and emails and replies go out again." actions={[{ label: "Resume", onSelect: () => void pause(false) }]} />
      <RangeSheet key={JSON.stringify(dates)} open={sheet === "range"} onClose={close} dates={dates} launchedAt={g.launchedAt} onPick={setDates} />
      {data && (
        <Sheet
          open={sheet === "all"}
          onClose={close}
          title="All of Meta's numbers"
          tall
          foot={
            c.mayAsk && (
              <button type="button" className="pn-btn soft wide" onClick={() => explain(`Explain all the ads together, ${dateLabel.toLowerCase()}`)}>
                <Icon name="ask" />
                Explain these numbers
              </button>
            )
          }
        >
          <div className="gp-sheet-stack">
            <p className="gp-lede">
              {dateLabel}
              {data.range.from ? `, ${daysWords([new Date(`${data.range.from}T00:00:00`), new Date(`${data.range.to}T00:00:00`)])}` : ""}, as Meta reports it.
            </p>
            {live.length > 1 && (
              <section>
                <div className="pn-cap">Where the results came from</div>
                <div className="pn-grp">
                  {live.map((i) => (
                    <PnRow key={i} title={ads[i].name} value={`${pctOf(perAd[i].results, setMain.results)}%`} />
                  ))}
                </div>
              </section>
            )}
            <MetricRows m={setMain} f={f} row={data.set} perAd={false} video={videos > 0 ? (videos < ads.length ? `${videos} of ${ads.length} ads` : "") : null} post={null} added={added} fields={fields} onRemove={toggleField} />
            <div className="pn-grp">
              <PnRow onClick={() => addNumber("all")} icon={<Icon name="plus" />} title="Add a number" chevron />
              <button type="button" className="pn-row" disabled={busy} onClick={() => void load(dates, true)}>
                <span className="pn-ico plain">
                  <Icon name="again" />
                </span>
                <span className="m">
                  <span className="t">{busy ? "Reading…" : "Read again from Meta"}</span>
                  <span className="s">Read {new Date(data.readAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                </span>
              </button>
            </div>
            {data.dropped.length > 0 && <p className="pn-note">Meta does not give this ad account {data.dropped.length} of its fields.</p>}
          </div>
        </Sheet>
      )}
      <AddNumberSheet open={sheet === "add"} onClose={() => setSheet(addBack)} fields={fields} added={added} onToggle={toggleField} />
      <PlaceNative key={String(sheet === "place")} open={sheet === "place"} onClose={close} ads={ads.length} spots={chosen} onSave={saveSpots} />
      {data && ad && (
        <Sheet
          open={!!ad}
          onClose={() => setAdOpen(null)}
          title={ad.name}
          tall
          foot={
            c.mayAsk && ad.row && (perAd[adIdx].impressions ?? 0) > 0 ? (
              <button type="button" className="pn-btn soft wide" onClick={() => explain(`Explain ${ad.name}, ${dateLabel.toLowerCase()}`, ad.adId ?? ad.platformAdId)}>
                <Icon name="ask" />
                Explain this ad
              </button>
            ) : undefined
          }
        >
          <AdNumbers ad={ad} m={perAd[adIdx]} f={f} inWords={inWords} goalPath={goalPath} data={data} added={added} fields={fields} onRemove={toggleField} onAdd={() => addNumber(null)} />
        </Sheet>
      )}
      <NeedSheet goalId={g.id} needs={needs} config={config} ready={c.ready} focus={panel ? (panel.focus ?? needs[0]?.id ?? null) : null} review={review} onDecide={decide} onClose={() => setPanel(null)} />
      <Sheet open={wrap} onClose={() => setWrap(false)} title="Wrap-up">
        <div className="ga gw ccx gp-in">
          <h2 className="gp-in-h">{wrapTitle}</h2>
          {wrapBody}
        </div>
      </Sheet>
      {n.toast}
      {c.toast}
    </div>
  );
}

/** One ad's numbers in its sheet: the three that matter, the steps from shown to the result, each card, where it showed and every other number. */
function AdNumbers({ ad, m, f, inWords, goalPath, data, added, fields, onRemove, onAdd }: { ad: NonNullable<ReturnType<typeof useGoalNumbers>["data"]>["ads"][number]; m: Main; f: Fmt; inWords: string; goalPath: string; data: NonNullable<ReturnType<typeof useGoalNumbers>["data"]>; added: string[]; fields: Map<string, Field>; onRemove: (k: string) => void; onAdd: () => void }) {
  const ran = (m.impressions ?? 0) > 0 && !!ad.row;
  const isVideo = ad.shape === "video" || ad.shape === null;
  const cards = ad.shape === "carousel" && ad.cards ? cardNumbers(data.cards, ad.platformAdId, ad.cards) : null;
  const best = cards ? cards.reduce((b, x, j) => ((x.taps ?? 0) > (cards[b].taps ?? 0) ? j : b), 0) : -1;
  const where = placeNumbers(data.places?.ads ?? null, ad.platformAdId);
  const pill = reviewPill(ad);
  const steps = funnelSteps(m, "Results", isVideo);
  const can = useCan();
  return (
    <div className="gp-sheet-stack">
      {pill && (
        <p className="gp-lede">
          <span className={`gp-st ${pill[0]}`}>{pill[1]}</span>
        </p>
      )}
      {ad.review?.message && (
        <section className="pn-card gp-meta-msg">
          <span className="gp-k">Meta's message</span>
          {ad.review.message.title && <h3>{ad.review.message.title}</h3>}
          {ad.review.message.text && <p>{ad.review.message.text}</p>}
          {can("editor") && (
            <Link className="pn-btn sm soft" to={`${goalPath}/ready`}>
              Fix and resubmit
            </Link>
          )}
        </section>
      )}
      {!ran ? (
        <p className="pn-note">{pill?.[1] === "In review" ? "No numbers until Meta approves it." : `No numbers ${inWords}.`}</p>
      ) : (
        <>
          <Tiles
            items={[
              [f.money(m.spend), "Spent"],
              [f.count(m.results), "Results"],
              [f.money(m.costEach), "Each"],
            ]}
          />
          <section>
            <div className="pn-cap">From shown to the result</div>
            <div className="pn-grp">
              {steps.map(([l, v], i) => {
                const prev = i ? steps[i - 1][1] : null;
                return <PnRow key={l} title={l} sub={i > 0 && prev && v !== null ? `${f.pct(v / prev, v / prev < 0.1 ? 1 : 0)} of the step before` : undefined} value={f.count(v)} />;
              })}
            </div>
          </section>
          {cards && (
            <section>
              <div className="pn-cap">Each card</div>
              <div className="pn-grp">
                {cards.map((x, j) => (
                  <div className="pn-row static" key={x.name}>
                    <span className="m">
                      <span className="t">{x.name}</span>
                      <span className="s">
                        {x.taps === null ? "No taps yet" : `${f.count(x.taps)} taps`}
                        {x.ctr !== null ? ` · ${x.ctr.toFixed(2)}% CTR` : ""}
                      </span>
                    </span>
                    {j === best && (x.taps ?? 0) > 0 && <span className="pn-pill good">Most taps</span>}
                  </div>
                ))}
              </div>
            </section>
          )}
          {where.length > 0 && (
            <section>
              <div className="pn-cap">Where it showed</div>
              <div className="pn-grp">
                {where.slice(0, 4).map((s) => (
                  <PnRow key={s.key} title={s.name} sub={`${Math.round(s.share * 100)}% of the money`} value={s.costEach === null ? "No results" : `${f.money(s.costEach)} each`} />
                ))}
              </div>
            </section>
          )}
          <MetricRows m={m} f={f} row={ad.row} perAd video={isVideo ? "" : null} post={ad.shape === "post" ? postNumbers(ad.row) : null} added={added} fields={fields} onRemove={onRemove} />
          <div className="pn-grp">
            <PnRow onClick={onAdd} icon={<Icon name="plus" />} title="Add a number" chevron />
          </div>
        </>
      )}
    </div>
  );
}
